import apiClient from './client';

export type GoalDriftStatus = 'on_track' | 'drifting' | 'critical';
export type GoalSuggestedAction = 'buy' | 'sell' | 'hold';

export interface GoalAllocationInput {
  symbol: string;
  targetPct: number;
}

export interface GoalAllocation {
  symbol: string;
  targetPct: number;
  actualPct: number;
  driftPct: number;
  absoluteDriftPct: number;
  driftStatus: GoalDriftStatus;
  suggestedAction: GoalSuggestedAction;
  suggestedDeltaUsd: number;
}

export interface PortfolioGoal {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  rebalanceThresholdPct: number;
  driftStatus: GoalDriftStatus;
  suggestion: string;
  allocations: GoalAllocation[];
  createdAt: string;
  updatedAt: string;
}

export interface PortfolioGoalsResponse {
  userId: string;
  goalCount: number;
  maxGoals: number;
  goals: PortfolioGoal[];
}

export interface CreatePortfolioGoalInput {
  name: string;
  description?: string | null;
  isActive?: boolean;
  rebalanceThresholdPct?: number;
  allocations: GoalAllocationInput[];
}

export interface UpdatePortfolioGoalInput {
  name?: string;
  description?: string | null;
  isActive?: boolean;
  rebalanceThresholdPct?: number;
  allocations?: GoalAllocationInput[];
}

export interface CreatePortfolioGoalResponse {
  goal: PortfolioGoal;
  goalCount: number;
  maxGoals: number;
  compatibilitySync: {
    mirroredRebalancingTargets: true;
  };
}

export interface UpdatePortfolioGoalResponse {
  goal: PortfolioGoal;
  compatibilitySync: {
    mirroredRebalancingTargets: boolean;
  };
}

export interface DeletePortfolioGoalResponse {
  deleted: true;
  goalId: string;
  remainingGoalCount: number;
  activeGoalId: string | null;
  compatibilitySync: {
    mirroredRebalancingTargets: boolean;
  };
}

export async function getPortfolioGoals(userId: string): Promise<PortfolioGoalsResponse> {
  const { data } = await apiClient.get<PortfolioGoalsResponse>(`/analytics/portfolio-goals/${userId}`);
  return data;
}

export async function createPortfolioGoal(
  userId: string,
  payload: CreatePortfolioGoalInput,
): Promise<CreatePortfolioGoalResponse> {
  const { data } = await apiClient.post<CreatePortfolioGoalResponse>(
    `/analytics/portfolio-goals/${userId}`,
    payload,
  );
  return data;
}

export async function updatePortfolioGoal(
  userId: string,
  goalId: string,
  payload: UpdatePortfolioGoalInput,
): Promise<UpdatePortfolioGoalResponse> {
  const { data } = await apiClient.patch<UpdatePortfolioGoalResponse>(
    `/analytics/portfolio-goals/${userId}/${goalId}`,
    payload,
  );
  return data;
}

export async function deletePortfolioGoal(
  userId: string,
  goalId: string,
): Promise<DeletePortfolioGoalResponse> {
  const { data } = await apiClient.delete<DeletePortfolioGoalResponse>(
    `/analytics/portfolio-goals/${userId}/${goalId}`,
  );
  return data;
}
